import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { EXRLoader } from 'three/examples/jsm/loaders/EXRLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { Box3, Vector3 } from 'three';

const LOAD_ATTEMPTS = 10; // Sometimes the assets fail to load for no reason, so we retry a few times.

interface Assets {
	v1RobotScene: THREE.Group | null;
	v2RobotScene: THREE.Group | null;
	hdrTexture: THREE.DataTexture | null;
	labelTextures: Record<number, THREE.Texture>;
	emissiveMap: THREE.Texture | null;
	cardboardMaterial: THREE.MeshStandardMaterial | null;
	woodMaterial: THREE.MeshStandardMaterial | null;
	envMap: THREE.WebGLRenderTarget | null;
	texturesLoaded: boolean;
	materialsReady: boolean;
	lowPower: boolean;
}

const AssetsContext = createContext<Assets>({
	v1RobotScene: null,
	v2RobotScene: null,
	texturesLoaded: false,
	labelTextures: {},
	emissiveMap: null,
	cardboardMaterial: null,
	woodMaterial: null,
	envMap: null,
	hdrTexture: null,
	materialsReady: false,
	lowPower: false,
});

type AssetState = {
	hdrTexture: THREE.DataTexture | null;
	v1RobotScene: THREE.Group | null;
	v2RobotScene: THREE.Group | null;
	cardboardTexture: THREE.Texture | null;
	cardboardNormal: THREE.Texture | null;
	woodTexture: THREE.Texture | null;
	woodNormal: THREE.Texture | null;
	texturesLoaded: boolean;
};

type AssetAction =
	| { type: 'setHdrTexture'; payload: THREE.DataTexture }
	| { type: 'setRobotScene'; payload: THREE.Group }
	| { type: 'setRobotV2Scene'; payload: THREE.Group }
	| { type: 'setCardboardTexture'; payload: THREE.Texture }
	| { type: 'setCardboardNormal'; payload: THREE.Texture }
	| { type: 'setWoodTexture'; payload: THREE.Texture }
	| { type: 'setWoodNormal'; payload: THREE.Texture }
	| { type: 'setTexturesLoaded' };

function assetReducer(state: AssetState, action: AssetAction): AssetState {
	switch (action.type) {
		case 'setHdrTexture':
			return { ...state, hdrTexture: action.payload };
		case 'setRobotScene':
			return { ...state, v1RobotScene: action.payload };
		case 'setRobotV2Scene':
			return { ...state, v2RobotScene: action.payload };
		case 'setCardboardTexture':
			return { ...state, cardboardTexture: action.payload };
		case 'setCardboardNormal':
			return { ...state, cardboardNormal: action.payload };
		case 'setWoodTexture':
			return { ...state, woodTexture: action.payload };
		case 'setWoodNormal':
			return { ...state, woodNormal: action.payload };
		case 'setTexturesLoaded':
			return { ...state, texturesLoaded: true };
		default:
			return state;
	}
}

function normalizeModel(model: THREE.Group, targetSize = 1200) {
	// 1. compute its bounding box
	const box = new Box3().setFromObject(model);
	const size = new Vector3();
	box.getSize(size);
	const x = size.x;

	// 2. scale so that its largest dimension = targetSize
	const scale = targetSize / x;
	model.scale.setScalar(scale);

	// 3. recompute box after scale
	box.setFromObject(model);

	// 4. center pivot: shift model so its center sits at world (0,0,0)
	const center = new Vector3();
	box.getCenter(center);
	model.position.sub(center);

	// 5. align “ground” (min Y) to y=0
	box.setFromObject(model);
	model.position.y -= box.min.y;
}

const initialState: AssetState = {
	hdrTexture: null,
	v1RobotScene: null,
	v2RobotScene: null,
	cardboardTexture: null,
	cardboardNormal: null,
	woodTexture: null,
	woodNormal: null,
	texturesLoaded: false,
};

export const useAssets = () => useContext(AssetsContext);

type LoaderWithLoad<T> = {
	load(
		url: string,
		onLoad: (data: T) => void,
		onProgress?: (event: ProgressEvent<EventTarget>) => void,
		onError?: (error: any) => void
	): void;
};

function loadWithRetry<T>(
	loader: LoaderWithLoad<T>,
	url: string,
	attempts = LOAD_ATTEMPTS
): Promise<T> {
	return new Promise((resolve, reject) => {
		function attempt(n: number) {
			loader.load(
				url,
				(data: T) => resolve(data),
				undefined,
				(error: any) => {
					if (n > 1) {
						console.warn(`Retry loading ${url}. Attempts remaining: ${n - 1}`);
						attempt(n - 1);
					} else {
						reject(error);
					}
				}
			);
		}
		attempt(attempts);
	});
}

export const AssetsProvider: React.FC = ({ children }) => {
	const [state, dispatch] = useReducer(assetReducer, initialState);
	const lowPower = useMemo(() => {
		if (typeof navigator === 'undefined') return false;

		const deviceMemory = (navigator as any).deviceMemory || 4;
		const cores = navigator.hardwareConcurrency || 4;

		return deviceMemory <= 4 || cores <= 4;
	}, []);

	useEffect(() => {
		THREE.Cache.enabled = true;
		THREE.Cache.clear();

		const exrLoader = new EXRLoader();
		const dracoLoader = new DRACOLoader();
		dracoLoader.setDecoderPath('/static/models/draco/');
		const gltfLoader = new GLTFLoader();
		gltfLoader.setDRACOLoader(dracoLoader);
		const textureLoader = new THREE.TextureLoader();
		const textureVersion = lowPower ? '_tiny.jpg' : '.png';
		Promise.all([
			loadWithRetry(exrLoader, '/static/hdr/canary.exr'),
			loadWithRetry(gltfLoader, `/static/models/robot.glb`),
			loadWithRetry(gltfLoader, `/static/models/robot_v6.glb`),
			loadWithRetry(textureLoader, `/static/textures/cardboard${textureVersion}`),
			loadWithRetry(
				textureLoader,
				`/static/textures/cardboard_normal${textureVersion}`
			),
			loadWithRetry(textureLoader, `/static/textures/wood${textureVersion}`),
			loadWithRetry(
				textureLoader,
				`/static/textures/wood_normal${textureVersion}`
			),
		])
			.then(
				([hdr, robotv1, robotv2, cardboard, cardboardNorm, wood, woodNorm]) => {
					dispatch({ type: 'setHdrTexture', payload: hdr });
					dispatch({ type: 'setCardboardTexture', payload: cardboard });
					dispatch({ type: 'setCardboardNormal', payload: cardboardNorm });
					dispatch({ type: 'setWoodTexture', payload: wood });
					dispatch({ type: 'setWoodNormal', payload: woodNorm });

					const robot = robotv1.scene;
					normalizeModel(robot, 650);
					robot.position.y = 0;
					robot.traverse((child: THREE.Object3D) => {
						if ((child as THREE.Mesh).isMesh) {
							const mesh = child as THREE.Mesh;
							const mat = mesh.material as THREE.MeshStandardMaterial;
							if (mat?.isMeshStandardMaterial) {
								mat.envMapIntensity = 1.5;
								mat.needsUpdate = true;
							}
						}
					});
					dispatch({ type: 'setRobotScene', payload: robot });
					const robot2 = robotv2.scene;
					normalizeModel(robot2, (3429) + 762);
					robot2.traverse((child: THREE.Object3D) => {
						if ((child as THREE.Mesh).isMesh) {
							const mesh = child as THREE.Mesh;
							mesh.castShadow = true;
							mesh.receiveShadow = true;
							const mat = mesh.material as THREE.MeshStandardMaterial;
							if (mat?.isMeshStandardMaterial) {
								mat.envMapIntensity = 4;
								mat.needsUpdate = true;
								// mat.roughness = 3;
							}
						}
					});
					dispatch({ type: 'setRobotV2Scene', payload: robot2 });
					dispatch({ type: 'setTexturesLoaded' });
				}
			)
			.catch(console.error);
	}, []);

	const labelTextures = useMemo(() => {
		if (!state.cardboardTexture) return {};
		const result: Record<number, THREE.Texture> = {};

		for (const labelPos of [0, 1, 2, 3, 4, 5]) {
			const size = 256;
			const canvas = document.createElement('canvas');
			canvas.width = size;
			canvas.height = size;
			const ctx = canvas.getContext('2d');
			if (ctx && state.cardboardTexture.image) {
				ctx.drawImage(state.cardboardTexture.image, 0, 0, size, size);
				const margin = size * 0.25;
				ctx.fillStyle = '#FFFFFF';
				ctx.fillRect(margin, margin, size - 2 * margin, size - 2 * margin);
			}
			const texture = new THREE.CanvasTexture(canvas);
			texture.needsUpdate = true;
			result[labelPos] = texture;
		}

		return result;
	}, [state.cardboardTexture]);

	const emissiveMap = useMemo(() => {
		const size = 256;
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.fillStyle = '#000000';
			ctx.fillRect(0, 0, size, size);
			const margin = size * 0.25;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(margin, margin, size - 2 * margin, size - 2 * margin);
		}
		const texture = new THREE.CanvasTexture(canvas);
		texture.needsUpdate = true;
		return texture;
	}, []);

	const cardboardMaterial = useMemo(() => {
		if (!state.cardboardTexture || !state.cardboardNormal) return null;

		return new THREE.MeshStandardMaterial({
			map: state.cardboardTexture,
			normalMap: state.cardboardNormal,
			metalness: 0.3,
			roughness: 1,
		});
	}, [state.cardboardTexture, state.cardboardNormal]);

	const woodMaterial = useMemo(() => {
		if (!state.woodTexture || !state.woodNormal) return null;

		return new THREE.MeshStandardMaterial({
			map: state.woodTexture,
			normalMap: state.woodNormal,
			metalness: 0.3,
			roughness: 1,
		});
	}, [state.woodTexture, state.woodNormal]);

	const materialsReady =
		state.v1RobotScene &&
		state.v2RobotScene &&
		state.hdrTexture &&
		cardboardMaterial &&
		woodMaterial &&
		emissiveMap &&
		Object.keys(labelTextures).length > 0;

	useEffect(() => {
		return () => {
			Object.values(labelTextures).forEach(tex => tex.dispose());
			emissiveMap?.dispose();
			cardboardMaterial?.dispose();
			woodMaterial?.dispose();
		};
	}, []);

	return (
		<AssetsContext.Provider
			value={{
				v1RobotScene: state.v1RobotScene,
				v2RobotScene: state.v2RobotScene,
				texturesLoaded: state.texturesLoaded,
				hdrTexture: state.hdrTexture,
				cardboardMaterial,
				woodMaterial,
				labelTextures,
				emissiveMap,
				envMap: null,
				materialsReady: !!materialsReady,
				lowPower,
			}}
		>
			{materialsReady ? children : null}
		</AssetsContext.Provider>
	);
};
